// PrivacyPolicy.tsx
import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Lock } from 'lucide-react'

interface Props { dark: boolean; apiBase: string; productUrl: string }

const Section: React.FC<{ title: string; children: React.ReactNode; dark: boolean; id?: string }> = ({ title, children, dark, id }) => (
  <section id={id} className="mb-10">
    <h2 className={`text-xl font-bold mb-4 ${dark ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
    <div className={`text-base leading-relaxed space-y-3 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
      {children}
    </div>
  </section>
)

const PrivacyPolicy: React.FC<Props> = ({ dark }) => {
  const updated = 'January 1, 2026'

  return (
    <>
      <Helmet>
        <title>Larxnet | Privacy Policy</title>
        <meta name="description" content="Larxnet Privacy Policy. Learn how we collect, use, and protect your personal information." />
      </Helmet>

      <div className={`min-h-screen py-20 page-enter ${dark ? 'text-white' : 'text-gray-900'}`}>
        <div className="max-w-3xl mx-auto px-6">
          {/* Header */}
          <div className="mb-14 text-center">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 ${dark ? 'bg-purple-500/10' : 'bg-purple-50'}`}>
              <Lock className="w-8 h-8 text-purple-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-black mb-4">Privacy Policy</h1>
            <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
              Last updated: {updated}
            </p>
          </div>

          {/* Content */}
          <div className={`rounded-3xl border p-8 md:p-12 ${dark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200 shadow-sm'}`}>

            <Section title="1. Introduction" dark={dark}>
              <p>Larxnet ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.</p>
              <p>Please read this policy carefully. If you disagree with its terms, please discontinue use of the Platform.</p>
            </Section>

            <Section title="2. Information We Collect" dark={dark}>
              <p><strong className={dark ? 'text-white' : 'text-gray-900'}>Information you provide:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Account registration data (username, email, password)</li>
                <li>Contact form submissions</li>
                <li>Profile preferences and settings</li>
                <li>Payment information (processed securely by third-party providers)</li>
              </ul>
              <p><strong className={dark ? 'text-white' : 'text-gray-900'}>Information collected automatically:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Usage data and interaction logs</li>
                <li>Device information and browser type</li>
                <li>IP address and approximate location</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </Section>

            <Section title="3. How We Use Your Information" dark={dark}>
              <p>We use collected information to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Create and manage your account</li>
                <li>Provide, operate, and improve the Platform</li>
                <li>Send transactional and service communications</li>
                <li>Respond to your comments and questions</li>
                <li>Detect and prevent fraud and abuse</li>
                <li>Comply with legal obligations</li>
                <li>Analyze usage patterns to enhance user experience</li>
              </ul>
            </Section>

            <Section title="4. Cookies" id="cookies" dark={dark}>
              <p>We use cookies and similar tracking technologies to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Maintain your session and authentication state</li>
                <li>Remember your preferences (e.g., dark mode)</li>
                <li>Analyze Platform usage and performance</li>
                <li>Provide security features</li>
              </ul>
              <p>You can control cookies through your browser settings. Disabling certain cookies may affect Platform functionality.</p>
            </Section>

            <Section title="5. Data Sharing and Disclosure" dark={dark}>
              <p>We do not sell your personal information. We may share your information with:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong className={dark ? 'text-white' : 'text-gray-900'}>Service providers:</strong> Trusted third parties who assist in operating the Platform</li>
                <li><strong className={dark ? 'text-white' : 'text-gray-900'}>Legal compliance:</strong> When required by law or to protect rights</li>
                <li><strong className={dark ? 'text-white' : 'text-gray-900'}>Business transfers:</strong> In connection with mergers or acquisitions</li>
              </ul>
            </Section>

            <Section title="6. Data Security" dark={dark}>
              <p>We implement industry-standard security measures including:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>256-bit AES encryption for data at rest</li>
                <li>TLS/SSL encryption for data in transit</li>
                <li>Regular security audits and penetration testing</li>
                <li>Access controls and authentication requirements</li>
              </ul>
              <p>No transmission over the internet is 100% secure. We strive to use commercially acceptable means to protect your information but cannot guarantee absolute security.</p>
            </Section>

            <Section title="7. Data Retention" dark={dark}>
              <p>We retain your personal information for as long as necessary to provide the Services and comply with our legal obligations. When you delete your account, we will delete or anonymize your personal data within 90 days.</p>
            </Section>

            <Section title="8. Your Rights" dark={dark}>
              <p>Depending on your location, you may have rights to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Access the personal information we hold about you</li>
                <li>Correct inaccurate or incomplete data</li>
                <li>Request deletion of your personal data</li>
                <li>Object to or restrict processing</li>
                <li>Data portability</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p>To exercise these rights, contact us through our <a href="/contact" className="text-purple-400 hover:text-purple-300 underline">Contact page</a>.</p>
            </Section>

            <Section title="9. Children's Privacy" dark={dark}>
              <p>The Platform is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If we discover we have collected information from a child, we will delete it immediately.</p>
            </Section>

            <Section title="10. Third-Party Links" dark={dark}>
              <p>The Platform may contain links to third-party websites. We are not responsible for the privacy practices of those sites and encourage you to review their privacy policies.</p>
            </Section>

            <Section title="11. Changes to This Policy" dark={dark}>
              <p>We may update this Privacy Policy periodically. We will notify you of significant changes by updating the "Last updated" date. Your continued use of the Platform constitutes acceptance of the revised policy.</p>
            </Section>

            <Section title="12. Contact Us" dark={dark}>
              <p>If you have questions about this Privacy Policy or our data practices, please <a href="/contact" className="text-purple-400 hover:text-purple-300 underline">contact us</a>.</p>
            </Section>
          </div>
        </div>
      </div>
    </>
  )
}

export default PrivacyPolicy
